import { useRef, useCallback, useState } from "react";
import Webcam from "react-webcam";
import { Button } from "@/components/ui/button";
import { Camera, RefreshCw } from "lucide-react";
import { AspectRatio } from "@/components/ui/aspect-ratio";

interface CameraCaptureProps {
  onCapture: (file: File) => void;
  capturedImage: string | null;
  onRetake: () => void;
}

export function CameraCapture({
  onCapture,
  capturedImage,
  onRetake,
}: CameraCaptureProps) {
  const webcamRef = useRef<Webcam>(null);
  const [isCameraActive, setIsCameraActive] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Video constraints for mobile optimization
  const videoConstraints: MediaTrackConstraints = {
    facingMode: "user",
    aspectRatio: 1,
    width: { ideal: 1280 },
    height: { ideal: 720 },
  };

  const handleUserMediaError = useCallback((error: string | DOMException) => {
    console.error("Camera error:", error);
    setIsCameraActive(false);

    const errorMessage = "Unable to access camera. ";
    setError(errorMessage);
  }, []);

  const handleUserMedia = useCallback(() => {
    setIsCameraActive(true);
    setError(null);
  }, []);

  const capturePhoto = useCallback(() => {
    const imageSrc = webcamRef.current?.getScreenshot();

    if (!imageSrc) {
      setError("Failed to capture photo. Please try again.");
      return;
    }

    // Convert base64 to File object
    fetch(imageSrc)
      .then((res) => res.blob())
      .then((blob) => {
        const file = new File([blob], "kyc-selfie.jpg", {
          type: "image/jpeg",
        });
        onCapture(file);
        setIsCameraActive(false);
      })
      .catch((err) => {
        console.error("Error converting image:", err);
        setError("Failed to process photo. Please try again.");
      });
  }, [onCapture]);

  const handleRetake = useCallback(() => {
    onRetake();
    setIsCameraActive(true);
  }, [onRetake]);

  const handleStopCamera = useCallback(() => {
    setIsCameraActive(false);
    setError(null);
  }, []);

  if (capturedImage) {
    return (
      <div className="space-y-4">
        <AspectRatio
          ratio={1}
          className="relative  bg-gray-100 rounded-lg overflow-hidden"
        >
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src={capturedImage}
            alt="Captured"
            className="w-full h-full object-cover"
          />
        </AspectRatio>
        <Button
          type="button"
          variant="outline"
          className="w-full rounded-full"
          onClick={handleRetake}
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          Retake Photo
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <AspectRatio
        ratio={1}
        className="relative  bg-gray-900 rounded-lg overflow-hidden"
      >
        {isCameraActive ? (
          <Webcam
            ref={webcamRef}
            audio={false}
            screenshotFormat="image/jpeg"
            screenshotQuality={1}
            videoConstraints={videoConstraints}
            onUserMedia={handleUserMedia}
            onUserMediaError={handleUserMediaError}
            className="w-full h-full object-cover"
            mirrored={true}
          />
        ) : (
          <div className="flex items-center justify-center h-full text-gray-400">
            <div className="text-center space-y-2">
              <Camera className="w-12 h-12 mx-auto" />
              <p className="text-sm">Camera not started</p>
            </div>
          </div>
        )}
      </AspectRatio>

      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
          {error}
        </div>
      )}

      <div className="flex gap-3">
        {!isCameraActive ? (
          <>
            <Button
              type="button"
              variant="outline"
              className="flex-1 rounded-full"
              onClick={() => setIsCameraActive(true)}
            >
              <Camera className="w-4 h-4 mr-2" />
              Start Camera
            </Button>
          </>
        ) : (
          <div className="flex gap-3 w-full">
            <Button
              type="button"
              className="flex-1 rounded-full"
              onClick={capturePhoto}
            >
              <Camera className="w-4 h-4 mr-2" />
              Capture Photo
            </Button>
            <Button
              type="button"
              variant="outline"
              className="rounded-full"
              onClick={handleStopCamera}
            >
              Cancel
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
